import { isGridSpecialColumn, refreshVisibleColumns } from '../column/meta'
import {
  createFilterCondition,
  createFilterGroup,
} from '../filter/filter-group'
import { getOperator, resolveFilterColumnType } from '../filter/filter-meta'
import { isFilterableColumn } from '../filter/filter-utils'
import type {
  DataGrid,
  GridAiAssistantOptions,
  GridAiAssistantStore,
  GridAiCommand,
  GridAiConversation,
  GridAiFilterItem,
  GridAiFilters,
  GridAiPromptContext,
  GridAiPromptContextColumn,
  GridAiSort,
  GridColumnInfo,
  GridFilterOperator,
  GridOptions,
} from '../types'

const SENSITIVE_FIELD_RE = /password|secret|token|credential|passwd/i
const SYSTEM_OPERATORS = new Set<GridFilterOperator>(['='])

function createAiId(prefix: string) {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
}

export function createAiAssistantStore(): GridAiAssistantStore {
  return {
    visible: false,
    sending: false,
    input: '',
    historyVisible: false,
    activeConversationId: null,
    conversations: [],
    suggestions: [],
  }
}

export function resolveAiAssistantOptions(
  options: Pick<GridOptions, 'aiAssistant'> | null | undefined
): GridAiAssistantOptions | null {
  const cfg = options?.aiAssistant
  if (!cfg || typeof cfg.onPrompt !== 'function') {
    return null
  }
  return cfg
}

export function isAiAssistantEnabled(
  options: Pick<GridOptions, 'aiAssistant'> | null | undefined
) {
  return resolveAiAssistantOptions(options) != null
}

function isAiColumn(column: GridColumnInfo) {
  return Boolean(column.field) && !isGridSpecialColumn(column)
}

function isSortableColumn(column: GridColumnInfo) {
  return isAiColumn(column) && column.sortable !== false
}

function findColumnByField(columns: GridColumnInfo[], field: string) {
  return columns.find((col) => col.field === field && isAiColumn(col))
}

function isSensitiveField(field: string) {
  return SENSITIVE_FIELD_RE.test(field)
}

function allowedOperators(column: GridColumnInfo): GridFilterOperator[] {
  const type = resolveFilterColumnType(column.type ?? 'base')
  return getOperator(type).map((item) => item.value)
}

function collectExamples(
  rows: Record<string, unknown>[],
  field: string,
  sampleSize: number
) {
  if (sampleSize <= 0 || isSensitiveField(field)) {
    return undefined
  }
  const seen = new Set<string>()
  const examples: unknown[] = []
  for (const row of rows) {
    const value = row[field]
    if (value == null || value === '') {
      continue
    }
    const key = typeof value === 'object' ? JSON.stringify(value) : String(value)
    if (seen.has(key)) {
      continue
    }
    seen.add(key)
    examples.push(value)
    if (examples.length >= sampleSize) {
      break
    }
  }
  return examples.length ? examples : undefined
}

export function buildGridAiSuggestions(
  columns: GridColumnInfo[],
  custom?: string[]
): string[] {
  if (custom?.length) {
    return custom.slice(0, 6)
  }
  const result: string[] = []
  const sortable = columns.find(isSortableColumn)
  if (sortable?.title) {
    result.push(`按${sortable.title}排序`)
  }
  const switchCol = columns.find((col) => col.type === 'switch' && isAiColumn(col))
  if (switchCol?.title) {
    result.push(`只看${switchCol.title}`)
  }
  const textCol = columns.find(
    (col) =>
      isFilterableColumn(col) &&
      col.field !== sortable?.field &&
      (col.type === 'input' || col.type === 'base' || !col.type)
  )
  if (textCol?.title) {
    result.push(`搜索${textCol.title}`)
  }
  return result.slice(0, 3)
}

export function buildGridAiPromptContext($grid: DataGrid): GridAiPromptContext {
  const cfg = resolveAiAssistantOptions($grid.options)
  const sampleSize = cfg?.sampleSize ?? 0
  const columns = $grid.internalData.allColumns.filter(isAiColumn)
  const rows = $grid.reactData.tableData ?? []

  const contextColumns: GridAiPromptContextColumn[] = columns.map((col) => {
    const field = col.field!
    return {
      field,
      title: col.title || field,
      type: col.type || 'input',
      filterable: isFilterableColumn(col),
      sortable: isSortableColumn(col),
      operators: allowedOperators(col),
      examples: collectExamples(rows, field, sampleSize),
    }
  })

  return {
    tableId: $grid.options.tableId,
    module: $grid.options.module,
    locale: 'zh-CN',
    columns: contextColumns,
    current: {
      searchKeyword: $grid.reactData.searchKeyword ?? '',
      sorts: $grid.getSorts?.() ?? [],
      filters: $grid.getFilters?.() ?? null,
      visibleFields: columns
        .filter((col) => col.visible !== false)
        .map((col) => col.field!),
      total: $grid.reactData.total ?? 0,
    },
    additionalContext: cfg?.additionalContext,
  }
}

export function validateGridAiCommand(
  command: GridAiCommand,
  columns: GridColumnInfo[]
): { ok: true; command: GridAiCommand } | { ok: false; message: string } {
  if (!command || typeof command !== 'object') {
    return { ok: false, message: 'AI 返回格式无效' }
  }
  const intents: GridAiCommand['intent'][] = [
    'operate',
    'explain',
    'clarify',
    'unsupported',
  ]
  if (!intents.includes(command.intent)) {
    return { ok: false, message: '无法识别该指令类型' }
  }

  if (command.sorts) {
    for (const sort of command.sorts) {
      const col = findColumnByField(columns, sort.field)
      if (!col || !isSortableColumn(col)) {
        return { ok: false, message: `无法排序列「${sort.field}」` }
      }
      if (sort.order !== 'asc' && sort.order !== 'desc') {
        return { ok: false, message: `排序方向无效：${sort.order}` }
      }
    }
  }

  if (command.filters?.items?.length) {
    for (const item of command.filters.items) {
      const error = validateFilterItem(item, columns)
      if (error) {
        return { ok: false, message: error }
      }
    }
  }

  if (command.columns?.length) {
    for (const item of command.columns) {
      if (!findColumnByField(columns, item.field)) {
        return { ok: false, message: `无法识别列「${item.field}」` }
      }
    }
    const nextVisible = new Set(
      columns
        .filter((col) => isAiColumn(col) && col.visible !== false)
        .map((col) => col.field!)
    )
    for (const item of command.columns) {
      if (item.visible) {
        nextVisible.add(item.field)
      } else {
        nextVisible.delete(item.field)
      }
    }
    if (nextVisible.size === 0) {
      return { ok: false, message: '至少保留一列可见' }
    }
  }

  return { ok: true, command }
}

function validateFilterItem(
  item: GridAiFilterItem,
  columns: GridColumnInfo[]
): string | null {
  const col = findColumnByField(columns, item.field)
  if (!col || !isFilterableColumn(col)) {
    return `无法筛选列「${item.field}」`
  }
  if (SYSTEM_OPERATORS.has(item.operator)) {
    return `不支持的筛选运算符：${item.operator}`
  }
  const allowed = allowedOperators(col)
  if (!allowed.includes(item.operator)) {
    return `列「${col.title || item.field}」不支持运算符 ${item.operator}`
  }
  if (item.operator !== 'empty' && item.operator !== 'notEmpty') {
    if (item.value == null || item.value === '') {
      return `列「${col.title || item.field}」缺少筛选值`
    }
  }
  return null
}

export function applyGridAiSorts(
  $grid: DataGrid,
  sorts: GridAiSort[] | null | undefined,
  query = true
) {
  if (sorts === undefined) {
    return
  }
  const { internalData, reactData } = $grid
  const next = sorts ?? []
  internalData.sortMap = {}
  for (const col of internalData.allColumns) {
    if (!col.field) {
      continue
    }
    const match = next.find((item) => item.field === col.field)
    if (match) {
      col.order = match.order
      internalData.sortMap[col.field] = col
    } else {
      col.order = null
    }
  }
  if (query) {
    reactData.currentPage = 1
    void $grid.queryByExamplePage()
  }
}

export function applyGridAiFilters(
  $grid: DataGrid,
  filters: GridAiFilters | null | undefined
) {
  if (filters === undefined) {
    return
  }
  if (!filters || !filters.items.length) {
    $grid.reactData.filterGroups = []
    return
  }
  const group = createFilterGroup()
  group.logic = filters.logic === 'or' ? 'or' : 'and'
  group.groupLogic = 'and'
  group.conditions = []
  for (const item of filters.items) {
    const col = findColumnByField($grid.internalData.allColumns, item.field)
    if (!col) {
      continue
    }
    const cond = createFilterCondition(col)
    cond.operator = item.operator
    cond.value = item.operator === 'empty' || item.operator === 'notEmpty'
      ? null
      : (item.value ?? null)
    cond.logic = group.logic
    group.conditions.push(cond)
  }
  $grid.reactData.filterGroups = group.conditions.length ? [group] : []
}

export function applyGridAiSearch(
  $grid: DataGrid,
  searchKeyword: string | null | undefined
) {
  if (searchKeyword === undefined) {
    return
  }
  $grid.reactData.searchKeyword = searchKeyword ?? ''
}

export function applyGridAiColumns(
  $grid: DataGrid,
  columns: GridAiCommand['columns']
) {
  if (!columns?.length) {
    return
  }
  const map = new Map(columns.map((item) => [item.field, item.visible]))
  for (const col of $grid.internalData.allColumns) {
    if (!col.field || isGridSpecialColumn(col) || !map.has(col.field)) {
      continue
    }
    col.visible = map.get(col.field) !== false
  }
  refreshVisibleColumns($grid)
  $grid.scheduleColumnWidthRecalc?.()
}

export async function applyGridAiCommand(
  $grid: DataGrid,
  command: GridAiCommand,
  options?: { query?: boolean }
) {
  const validated = validateGridAiCommand(
    command,
    $grid.internalData.allColumns
  )
  if (!validated.ok) {
    throw new Error(validated.message)
  }
  const safe = validated.command
  if (safe.intent !== 'operate') {
    return safe
  }
  const query = options?.query !== false
  applyGridAiFilters($grid, safe.filters)
  applyGridAiSorts($grid, safe.sorts, false)
  applyGridAiSearch($grid, safe.searchKeyword)
  applyGridAiColumns($grid, safe.columns)
  if (query) {
    $grid.reactData.currentPage = 1
    await $grid.queryByExamplePage()
  }
  return safe
}

function ensureConversation($grid: DataGrid): GridAiConversation {
  const store = $grid.reactData.aiAssistantStore
  const existing = store.conversations.find(
    (item) => item.id === store.activeConversationId
  )
  if (existing) {
    return existing
  }
  const conversation: GridAiConversation = {
    id: createAiId('conv'),
    title: '',
    prompts: [],
  }
  store.conversations.unshift(conversation)
  store.activeConversationId = conversation.id
  return conversation
}

export function useAiAssistant($grid: DataGrid) {
  const { reactData, options } = $grid

  $grid.buildAiPromptContext = () => buildGridAiPromptContext($grid)

  $grid.applyAiCommand = (command, queryOptions) =>
    applyGridAiCommand($grid, command, queryOptions)

  $grid.openAiAssistant = () => {
    const cfg = resolveAiAssistantOptions(options)
    if (!cfg) {
      return
    }
    reactData.initStore.aiAssistant = true
    reactData.aiAssistantStore.visible = true
    reactData.aiAssistantStore.historyVisible = false
    if (!reactData.aiAssistantStore.suggestions.length) {
      reactData.aiAssistantStore.suggestions = buildGridAiSuggestions(
        $grid.internalData.allColumns,
        cfg.suggestions
      )
    }
    ensureConversation($grid)
  }

  $grid.closeAiAssistant = () => {
    reactData.aiAssistantStore.visible = false
    reactData.aiAssistantStore.historyVisible = false
  }

  $grid.toggleAiAssistant = () => {
    if (!isAiAssistantEnabled(options)) {
      return
    }
    if (reactData.aiAssistantStore.visible) {
      $grid.closeAiAssistant?.()
      return
    }
    $grid.openAiAssistant?.()
  }

  $grid.startAiConversation = () => {
    const store = reactData.aiAssistantStore
    const conversation: GridAiConversation = {
      id: createAiId('conv'),
      title: '',
      prompts: [],
    }
    store.conversations.unshift(conversation)
    store.activeConversationId = conversation.id
    store.historyVisible = false
    store.input = ''
  }

  $grid.submitAiPrompt = async (query) => {
    const cfg = resolveAiAssistantOptions(options)
    if (!cfg) {
      return
    }
    const store = reactData.aiAssistantStore
    const text = (query ?? store.input).trim()
    if (!text || store.sending) {
      return
    }
    store.sending = true
    const conversation = ensureConversation($grid)
    const promptId = createAiId('prompt')
    conversation.prompts.push({
      id: promptId,
      value: text,
      createdAt: Date.now(),
      variant: 'processing',
    })
    if (!conversation.title) {
      conversation.title = text.slice(0, 24)
    }
    store.input = ''
    store.sending = true
    store.historyVisible = false

    const prompt = conversation.prompts.find((item) => item.id === promptId)
    try {
      const context = buildGridAiPromptContext($grid)
      const command = await cfg.onPrompt(text, context, conversation.id)
      const applied = await applyGridAiCommand($grid, {
        ...command,
        conversationId: command.conversationId || conversation.id,
      })
      if (prompt) {
        prompt.variant =
          applied.intent === 'unsupported' || applied.intent === 'clarify'
            ? applied.intent === 'unsupported'
              ? 'error'
              : 'success'
            : 'success'
        prompt.helperText = applied.explanation || ''
      }
      if (applied.suggestions?.length) {
        store.suggestions = applied.suggestions.slice(0, 6)
      }
    } catch (error) {
      if (prompt) {
        prompt.variant = 'error'
        prompt.helperText =
          error instanceof Error && error.message
            ? error.message
            : '无法处理该指令'
      }
    } finally {
      store.sending = false
    }
  }
}
